import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { fmtC, toLocalDateStr } from '../../lib/utils';
import { invoicePaidDate, isInvoiceOverdue } from '../../lib/financialsEngine';
import { useInvoices } from '../../hooks/useInvoices';
import { useFocus } from '../../hooks/useFocus';

export default function InvoiceStatsBar() {
  const { data: invoices = [] } = useInvoices();
  const { isHidden } = useFocus();
  const hideOverdue = isHidden('invoices.overdueCard');

  const stats = useMemo(() => {
    const now = new Date();
    const today = toLocalDateStr(now);
    const monthStart = toLocalDateStr(new Date(now.getFullYear(), now.getMonth(), 1));
    let outstanding = 0, overdue = 0, paidThisMonth = 0, totalBilled = 0;

    invoices.forEach((inv) => {
      const balance = (parseFloat(inv.amount) || 0) - (parseFloat(inv.paidAmount) || 0);
      if (['unpaid', 'sent', 'partial'].includes(inv.status)) outstanding += balance;
      if (isInvoiceOverdue(inv, today)) overdue += balance;
      if (inv.status === 'paid') {
        const paidDate = invoicePaidDate(inv);
        if (paidDate && paidDate >= monthStart) paidThisMonth += (parseFloat(inv.amount) || 0);
      }
      if (inv.status !== 'void') totalBilled += (parseFloat(inv.amount) || 0);
    });

    return { outstanding, overdue, paidThisMonth, totalBilled };
  }, [invoices]);

  const cards = [
    { label: 'Outstanding', value: stats.outstanding, color: 'warning.main' },
    { label: 'Overdue', value: stats.overdue, color: 'error.main', focus: 'invoices.overdueCard' },
    { label: 'Paid (Month)', value: stats.paidThisMonth, color: 'success.main' },
    { label: 'Total Billed', value: stats.totalBilled, color: 'info.main' },
  ].filter((c) => !(hideOverdue && c.focus));

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: hideOverdue ? '1fr 1fr 1fr' : '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
      {cards.map((c, i) => (
        <Paper key={c.label} data-focus={c.focus} sx={{ p: 2, ...(hideOverdue && i === cards.length - 1 && { gridColumn: { xs: 'span 2', md: 'auto' } }) }}>
          <Typography variant="overline" color="text.secondary">{c.label}</Typography>
          <Typography variant="h5" sx={{ color: c.color, fontWeight: 700 }}>{fmtC(c.value)}</Typography>
        </Paper>
      ))}
    </Box>
  );
}
